import {
  buildOutlinedFigure,
  clamp01,
  ellipseSegment,
  polylineSegment,
  selectNodalTargets,
  smoothstep,
  superellipseSegment,
  type ChladniMode,
} from "./cymaticGeometry"

const SQUIRCLE_EXPONENT = 4.4
const INTERIOR_EXTENT = 0.58
const SOFIA_LOGO_FIGURE_ID = "sofia-logo"

export const CYMATIC_SCENE_MS = 1800

export interface CymaticFigure {
  id: string
  label: string
  points: Float32Array
}

interface CymaticTransition {
  fromIndex: number
  toIndex: number
  progress: number
  energy: number
}

type ModeSpec = readonly [number, "cos" | "sin", number, number, -1 | 1, number]

const MODE_SPECS: readonly ModeSpec[] = [
  [1, "cos", 1, 2, -1, 0],
  [2, "cos", 1, 3, -1, 0],
  [3, "cos", 2, 3, -1, 0],
  [4, "cos", 1, 4, -1, 0],
  [5, "cos", 2, 5, -1, 24],
  [6, "cos", 3, 4, -1, 0],
  [7, "cos", 3, 5, -1, 24],
  [8, "cos", 4, 5, -1, 48],
  [9, "cos", 2, 7, -1, 48],
  [10, "cos", 5, 6, -1, 48],
]

const CHLADNI_MODES: readonly ChladniMode[] = MODE_SPECS.map(
  ([number, basis, n, m, sign, minSize]) => {
    const suffix = String(number).padStart(2, "0")
    return {
      id: `chladni-${suffix}`,
      label: `Chladni ${suffix} · ${n}:${m} ${basis === "cos" ? "cosine" : "sine"} ${sign === 1 ? "sum" : "difference"}`,
      basis,
      n,
      m,
      sign,
      minSize,
    }
  },
)

export const SLACK_SOURCE_CHLADNI_IDS = [
  "chladni-01",
  "chladni-02",
  "chladni-03",
  "chladni-05",
] as const

/**
 * Sofia's own mark, then the plates. The reel is Chladni figures only — the
 * animation is a resonance plate resolving, so a recognizable glyph morphing out
 * of it reads as a different picture rather than the same field settling.
 */
const FIGURE_ORDER: readonly string[] = [
  SOFIA_LOGO_FIGURE_ID,
  ...CHLADNI_MODES.map((mode) => mode.id),
]

const CHLADNI_BY_ID = new Map<string, ChladniMode>(
  CHLADNI_MODES.map((mode) => [mode.id, mode]),
)
const CATALOG_CACHE = new Map<string, readonly CymaticFigure[]>()

const DEG_45 = Math.PI / 4
const SQUIRCLE = superellipseSegment(0.72, 0.72, SQUIRCLE_EXPONENT)
const SQUARE = polylineSegment([
  [-0.68, -0.68],
  [0.68, -0.68],
  [0.68, 0.68],
  [-0.68, 0.68],
])
const LOGO_LOOPS = [
  ellipseSegment(0.24, 0.53, DEG_45),
  ellipseSegment(0.24, 0.53, -DEG_45),
]
const LOGO_DIAMOND = superellipseSegment(0.29, 0.29, 4.2, DEG_45)

/**
 * How many particles a field of this pixel size draws.
 *
 * A figure needs enough points to read as a nodal pattern, but the count has to
 * fall off faster than the box does: particles have a floor radius of half a
 * pixel, so a count chosen for a 64px mark packs a 14px one solid and the
 * figure reads as a speckled block rather than a shape. Density therefore
 * tracks area below 40px and the shared linear rate at or above it, which is
 * where the two agree.
 */
export function getCymaticParticleCount(size: number) {
  return Math.round(Math.max(24, Math.min(size * 2.4, size * size * 0.06)))
}

export function getCymaticFigureIds(size: number) {
  return FIGURE_ORDER.filter((id) => {
    const mode = CHLADNI_BY_ID.get(id)
    return mode ? size >= mode.minSize : true
  })
}

export function buildCymaticFigureCatalog(count: number, size: number) {
  const cacheKey = `${count}:${size < 24 ? "small" : size < 48 ? "medium" : "large"}`
  const cached = CATALOG_CACHE.get(cacheKey)
  if (cached) {
    return cached
  }

  const figures = getCymaticFigureIds(size).map((id): CymaticFigure => {
    const mode = CHLADNI_BY_ID.get(id)
    if (!mode) {
      return buildSofiaLogoFigure(count)
    }
    return {
      id,
      label: mode.label,
      points: buildOutlinedFigure(count, SQUARE, (innerCount) =>
        selectNodalTargets(mode, innerCount, INTERIOR_EXTENT),
      ),
    }
  })
  CATALOG_CACHE.set(cacheKey, figures)
  return figures
}

/**
 * The particle rendition of Sofia's resting mark. It is deliberately absent from
 * the thinking reel: the field starts here as it emerges from the static mark
 * and returns here as it settles back, so the crossfade to the SVG lines up.
 */
export function buildSofiaLogoFigure(count: number): CymaticFigure {
  return {
    id: SOFIA_LOGO_FIGURE_ID,
    label: "Current Sofia mark",
    points: buildOutlinedFigure(count, SQUIRCLE, [...LOGO_LOOPS, LOGO_DIAMOND]),
  }
}

function shuffled<T>(items: readonly T[], random: () => number) {
  const result = Array.from(items)
  for (let index = result.length - 1; index > 0; index -= 1) {
    const value = Math.min(0.999999, Math.max(0, random()))
    const swapIndex = Math.floor(value * (index + 1))
    ;[result[index], result[swapIndex]] = [result[swapIndex], result[index]]
  }
  return result
}

/**
 * Builds one complete thinking reel: every Chladni plate eligible at this size,
 * in a fresh order. Sofia's own mark is not part of the reel — it only anchors
 * the resting state, so the reel never morphs through the logo. Supplying the
 * previous order guarantees a different order for the next run even if the
 * random source repeats itself.
 */
export function buildCymaticFigureReel(
  count: number,
  size: number,
  random: () => number = Math.random,
  previousIds: readonly string[] = [],
) {
  const catalog = buildCymaticFigureCatalog(count, size)
  const reel = shuffled(
    catalog.filter((figure) => figure.id !== SOFIA_LOGO_FIGURE_ID),
    random,
  )

  if (
    reel.length > 1 &&
    reel.length === previousIds.length &&
    reel.every((figure, index) => figure.id === previousIds[index])
  ) {
    return [...reel.slice(1), reel[0]]
  }

  return reel
}

export function getCymaticTransition(
  elapsedMs: number,
  figureCount: number,
): CymaticTransition {
  const safeCount = Math.max(1, figureCount)
  const safeElapsed = Math.max(0, elapsedMs)
  const sceneIndex = Math.floor(safeElapsed / CYMATIC_SCENE_MS) % safeCount
  const phase = (safeElapsed % CYMATIC_SCENE_MS) / CYMATIC_SCENE_MS
  const progress =
    phase <= 0.22 ? 0 : phase >= 0.78 ? 1 : smoothstep((phase - 0.22) / 0.56)
  return {
    fromIndex: sceneIndex,
    toIndex: (sceneIndex + 1) % safeCount,
    progress,
    energy: Math.sin(progress * Math.PI),
  }
}

export function writeCymaticTargets(
  from: Float32Array,
  to: Float32Array,
  transition: Pick<CymaticTransition, "progress" | "energy">,
  targetX: Float32Array,
  targetY: Float32Array,
) {
  const count = Math.min(from.length, to.length) / 2
  const progress = clamp01(transition.progress)
  const arc = transition.energy * 0.075
  const scale = 1 + transition.energy * 0.035
  for (let index = 0; index < count; index += 1) {
    const offset = index * 2
    const x = from[offset] + (to[offset] - from[offset]) * progress
    const y = from[offset + 1] + (to[offset + 1] - from[offset + 1]) * progress
    const direction = index % 2 === 0 ? 1 : -1
    targetX[index] = (x - y * arc * direction) * scale
    targetY[index] = (y + x * arc * direction) * scale
  }
}
